import { useCallback, useEffect, useRef, useState } from 'react';
import { ApiError } from '../../../../services/api';
import {
  listScholarshipPlayers,
  reorderScholarshipPlayers,
} from '../../../../services/scholarships';
import type {
  ScholarshipPlayer,
  ScholarshipPlayerPosition,
} from '../../../../types/scholarships';
import { getSafeErrorMessage } from '../../../../utils/errorHandler';
import { diamondSortComparator } from './depthChartPositions';

export const isConflictError = (e: unknown): boolean =>
  e instanceof ApiError && e.statusCode === 409;

export const REORDER_CONFLICT_MESSAGE = 'Player set has changed. Refreshing…';

/**
 * The same order with `orderedIds` moved to the front of their position in that order, sorted the
 * way the server sorts. Pure, so the optimistic state is testable on its own.
 */
export const applyReorder = (
  players: readonly ScholarshipPlayer[],
  position: ScholarshipPlayerPosition,
  orderedIds: readonly string[],
): ScholarshipPlayer[] => {
  const indexById = new Map(orderedIds.map((id, idx) => [id, idx]));
  return players
    .map((p) =>
      p.position === position && indexById.has(p.id)
        ? { ...p, sortOrder: indexById.get(p.id)! }
        : p,
    )
    .sort(diamondSortComparator);
};

/**
 * Writes to the players list, for guarding reads. `generation` moves when a write starts or ends
 * (and on a local merge), `inFlight` counts the writes still running. Mirrors mobile's
 * useRosterBoard.
 */
interface WriteTracker {
  generation: number;
  inFlight: number;
}

/** Whether a read that started at `generation` is still safe to apply. */
const untouchedSince = (tracker: WriteTracker, generation: number) =>
  tracker.generation === generation && tracker.inFlight === 0;

export interface RosterPlayersState {
  players: ScholarshipPlayer[] | null;
  error: string | null;
  setError: (message: string | null) => void;
  refresh: () => Promise<void>;
  /** Local-only edit, for an optimistic change the caller rolls back itself. */
  setPlayers: React.Dispatch<React.SetStateAction<ScholarshipPlayer[] | null>>;
  /**
   * Reorder one position. Optimistic: the rows move at once, and move back if the save fails. A
   * 409 means the position's rows changed underneath (someone added or removed a player), so the
   * board is refetched rather than left showing an order the server refused.
   */
  reorder: (position: ScholarshipPlayerPosition, orderedIds: string[]) => Promise<void>;
  /**
   * Puts a row the API just returned into the list at once, keeping the local sort order (a drag
   * may have landed meanwhile). The player modal's save goes through this before the refetch, so
   * a control that writes the row back next (the pitching role, a full replace) builds on the
   * saved row rather than the one the modal opened with. A row not in the list (a new player) is
   * left to the refetch.
   */
  mergeSaved: (saved: ScholarshipPlayer | null) => void;
  /**
   * Runs a write to the players (a role save, a status change) so a refetch that overlaps it
   * cannot put the old row back. The optimistic edit belongs inside `write`. A refetch that
   * overlapped is dropped and run again once every write has settled.
   */
  trackWrite: <T>(write: () => Promise<T>) => Promise<T>;
  /**
   * A token for the list as it stands right now. Capture it BEFORE a request whose reply
   * describes the whole list, and hand it back to `replaceAll`.
   */
  listVersion: () => number;
  /**
   * Replaces the whole list with a response that describes the whole list, but only when nothing
   * has changed the list since `since` was captured.
   *
   * Returns false when the response was dropped as out of date, so the caller can apply the one
   * thing it still knows and read the board again. A reply that outlives the dialog that asked
   * for it -- a transfer the coach dismissed -- would otherwise put back a roster they have
   * already moved on from.
   */
  replaceAll: (next: readonly ScholarshipPlayer[], since: number) => boolean;
}

/**
 * A board's players, for every view of the board: load, refetch, the optimistic reorder with its
 * rollback, and the merge after a player save. One copy, so the Players tab, the depth chart, the
 * pitching staff and the lineups behave the same when a drag is refused.
 */
export const useRosterPlayers = (
  scholarshipId: string,
  onReordered?: () => void,
): RosterPlayersState => {
  const [players, setPlayersState] = useState<ScholarshipPlayer[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  /*
    Every settled change to the list, whatever caused it: a load landing, a merge, an optimistic
    edit, an attach that replaces the lot. Separate from the write generation above, which is
    about keeping a READ from clobbering a write. This one answers a different question -- "has
    anything at all moved since I sent this request" -- for a reply that describes the whole list
    and may outlive the dialog that asked for it. See replaceAll.
  */
  const listVersionRef = useRef(0);

  // Wrapped so that every local edit a caller makes moves the version. An attach replacing the
  // whole roster and a status tick are both "something newer landed" as far as a stale
  // whole-list reply is concerned, and neither goes through the write tracker.
  const setPlayers = useCallback<React.Dispatch<React.SetStateAction<ScholarshipPlayer[] | null>>>(
    (action) => {
      listVersionRef.current += 1;
      setPlayersState(action);
    },
    [],
  );

  const listVersion = useCallback(() => listVersionRef.current, []);

  const writes = useRef<WriteTracker>({ generation: 0, inFlight: 0 });
  // A refetch was asked for, or dropped, while a write was running: run it when they settle.
  const reloadPending = useRef(false);
  const requests = useRef(0);
  const loadRef = useRef<() => Promise<void>>(async () => {});

  // Loads without touching the message, so the conflict notice below survives its own refetch.
  const load = useCallback(async () => {
    const tracker = writes.current;
    if (tracker.inFlight > 0) {
      reloadPending.current = true;
      return;
    }
    const startedAt = tracker.generation;
    const ticket = ++requests.current;
    try {
      const loaded = await listScholarshipPlayers(scholarshipId);
      if (ticket !== requests.current) return;
      // A write that overlapped this read is newer than what it returned: keep the write's state
      // and read again once the writes are done.
      if (!untouchedSince(tracker, startedAt)) {
        if (tracker.inFlight > 0) reloadPending.current = true;
        else void loadRef.current();
        return;
      }
      setPlayers(loaded);
    } catch (e) {
      if (ticket !== requests.current) return;
      setError(getSafeErrorMessage(e, 'Failed to load players'));
    }
  }, [scholarshipId, setPlayers]);
  loadRef.current = load;

  const beginWrite = useCallback(() => {
    writes.current.generation += 1;
    writes.current.inFlight += 1;
  }, []);

  const endWrite = useCallback(() => {
    writes.current.generation += 1;
    writes.current.inFlight -= 1;
    if (writes.current.inFlight === 0 && reloadPending.current) {
      reloadPending.current = false;
      void loadRef.current();
    }
  }, []);

  const trackWrite = useCallback(
    async <T>(write: () => Promise<T>): Promise<T> => {
      beginWrite();
      try {
        return await write();
      } finally {
        endWrite();
      }
    },
    [beginWrite, endWrite],
  );

  const refresh = useCallback(async () => {
    setError(null);
    await load();
  }, [load]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const reorder = async (position: ScholarshipPlayerPosition, orderedIds: string[]) => {
    if (!players) return;
    // Only this position's order is put back on failure: a role or status saved meanwhile on any
    // row, this position's included, must survive the rollback.
    const previousOrder = new Map(
      players.filter((p) => p.position === position).map((p) => [p.id, p.sortOrder]),
    );
    beginWrite();
    let refetch = false;
    setPlayers(applyReorder(players, position, orderedIds));
    setError(null);
    try {
      await reorderScholarshipPlayers(scholarshipId, position, orderedIds);
      onReordered?.();
    } catch (e) {
      setPlayers((current) =>
        current
          ? current
              .map((p) =>
                p.position === position && previousOrder.has(p.id)
                  ? { ...p, sortOrder: previousOrder.get(p.id)! }
                  : p,
              )
              .sort(diamondSortComparator)
          : current,
      );
      if (isConflictError(e)) {
        setError(REORDER_CONFLICT_MESSAGE);
        refetch = true;
      } else {
        setError(getSafeErrorMessage(e, 'Failed to save new order'));
      }
    } finally {
      endWrite();
    }
    // After the write has ended, so the refetch is not dropped as overlapping it (or deferred
    // until any other write settles).
    if (refetch) void load();
  };

  const mergeSaved = useCallback((saved: ScholarshipPlayer | null) => {
    if (!saved) return;
    // A read already running predates this row.
    writes.current.generation += 1;
    setPlayers((current) =>
      current
        ? current.map((p) => (p.id === saved.id ? { ...saved, sortOrder: p.sortOrder } : p))
        : current,
    );
  }, [setPlayers]);

  const replaceAll = useCallback(
    (next: readonly ScholarshipPlayer[], since: number): boolean => {
      if (listVersionRef.current !== since) return false;
      setPlayers([...next].sort(diamondSortComparator));
      return true;
    },
    [setPlayers],
  );

  return {
    players,
    error,
    setError,
    refresh,
    setPlayers,
    reorder,
    mergeSaved,
    trackWrite,
    listVersion,
    replaceAll,
  };
};
